import { List } from "immutable";
import {
  getProp,
  gatherProps,
  isInheritedPropSynced,
  propIsSub,
  getSubParent,
  shouldPropInherit,
  propIsParent,
} from "./Merge/MergeUtils.mjs";
import {
  IS_DEVIATION,
  LAYOUTS,
  LAYOUT_DESKTOP,
  LAYOUT_MOBILE,
  MIXED,
  MIXED_SYNC,
} from "../constants/DesignConstants.mjs";
import { PROP_INHERITANCE_MAP } from "../constants/SyncConstants";
import { BUTTON, SIMPLE, TEXT } from "../constants/DataTypes";
import PropertyMaps from "../constants/PropertyMaps";
import { shouldSyncInherit, gatherSubPropList } from "./Merge/MergeUtils.mjs";
import { ensureArray } from "./JSUtils";
import AppConstants from "../constants/AppConstants";
import { isSynced } from "./SyncUtils";
import {
  BORDER_COLOR,
  BORDER_STYLE,
  BORDER_WIDTH,
  BUTTON_STYLE,
  TEXT_OVERFLOW,
  TEXT_STYLE,
  TEXT_STYLES,
} from "../constants/MergeConstants";
import { generateRemovePropUpdates } from "./SyncUtils";
import ImmutableUtils from "./ImmutableUtils";
import { redirectUpdate, getPropUpdateDetails, deleteProp } from "./PropUpdateUtils";
import UpdateBuilder from "./UpdateBuilder.mjs";

/**
 * @typedef {import("immutable").Map} ImmutableMap
 * @typedef {import("./MulticastUtils").PropUpdate} PropUpdate
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} LAYOUTS
 */

const { TEXT_STYLE_PROPS, TEXT_NESTED_STYLES_INLINE_LINK } = PropertyMaps;
const { SYNCED_PROPS, UPDATE_ACTION_SET, UPDATE_ACTION_DELETE, DEFAULT_BORDER_PROPERTIES } = AppConstants;

// todo: find a better home for this. Might need to omit text style
const INHERIT_PROP_LIST = {
  [BUTTON]: [...Object.keys(PROP_INHERITANCE_MAP[BUTTON])],
  [TEXT]: [TEXT_STYLE, ...TEXT_STYLE_PROPS, ...TEXT_NESTED_STYLES_INLINE_LINK],
};

const STYLE_PROP_NAME_MAP = {
  [BUTTON]: BUTTON_STYLE,
  [TEXT]: TEXT_STYLE,
};

const STYLE_SYNC_PROPS = {
  [BUTTON]: [...Object.keys(PROP_INHERITANCE_MAP[BUTTON])],
  [TEXT]: [...Object.keys(PROP_INHERITANCE_MAP[TEXT]), TEXT_OVERFLOW],
};

// keys to remove on style change
const STYLE_CHANGE_REMOVE_KEYS = {
  [BUTTON]: [...Object.keys(PROP_INHERITANCE_MAP[BUTTON])],
  [TEXT]: [...Object.keys(PROP_INHERITANCE_MAP[TEXT]), TEXT_STYLES],
};

/**
 * Can
 * @param {ImmutableMap} elementData
 * @returns
 */
export const isElementStyled = (elementData) => {
  const type = elementData.get("type");
  return type in STYLE_PROP_NAME_MAP;
};

// todo: maybe inject propList instead?
/**
 * Gets an element's sync state when it should be inherited.
 * @param {ImmutableMap} elementData
 * @param {ImmutableMap} theme
 * @returns {Array<String>}
 */
const getInheritedSync = (elementData, propList, theme) => {
  return propList.filter(
    (propName) =>
      shouldSyncInherit(elementData, propName, theme) &&
      isInheritedPropSynced(elementData, LAYOUT_DESKTOP, propName, theme)
  );
};

/**
 * Get the style prop name for a particular element type.
 * @param {ImmutableMap} elementData
 * @returns {typeof STYLE_PROP_NAME_MAP[keyof typeof STYLE_PROP_NAME_MAP]}
 */
export const getTypeStylePropName = (elementData) => {
  const type = elementData.get("type");
  return STYLE_PROP_NAME_MAP[type];
};

/**
 * Checks type to see if style can be split/merged. Assumes element has a style.
 * @param {ImmutableMap} elementData
 * @returns
 */
const canStyleSync = (elementData) => {
  const type = elementData.get("type");
  return type && type === TEXT;
};

/**
 * Checks to see if style is synced.
 * @param {ImmutableMap} elementData
 * @returns {boolean}
 */
export const isStyleSynced = (elementData) => {
  const stylePropName = getTypeStylePropName(elementData);
  return elementData.get(SYNCED_PROPS, List()).includes(stylePropName);
};

/** DEVIATION PROCESSES */

/**
 * Ultimately a check for should inherit on desktop. A deviated prop will have some value set on desktop.
 * @param {*} elementData
 * @param {*} propName
 * @param {*} theme
 * @returns
 */
export const isPropDeviated = (elementData, propName, theme) =>
  !shouldPropInherit(elementData, LAYOUT_DESKTOP, propName, theme);

/**
 * Checks if element deviates from its inheritable base.
 * @param {ImmutableMap} elementData
 * @returns {boolean}
 */
export const isDeviation = (elementData) => {
  const type = elementData.get("type");
  const propList = PROP_INHERITANCE_MAP[type] ?? [];

  for (const propName of Object.keys(propList)) {
    if (!LAYOUTS.every((layout) => getProp(elementData, layout, propName) === undefined))
      return true;
  }

  return false;
};

/**
 * Infers missing border properties for Simple elements on each layout independently.
 * Ensures complete border data (bS, bW, bC) when any border property exists on a layout.
 *
 * @param {ImmutableMap} elementData
 * @returns {ImmutableMap}
 */
const inferBorderPropsForSimple = (elementData) => {
  return LAYOUTS.reduce((result, layout) => {
    const layoutData = result.get(layout);
    if (!layoutData) return result;

    const borderProps = gatherProps(result, layout, [BORDER_WIDTH, BORDER_COLOR, BORDER_STYLE]);
    const width = borderProps.get(BORDER_WIDTH);
    const color = borderProps.get(BORDER_COLOR);
    const style = borderProps.get(BORDER_STYLE);

    const hasWidth = typeof width === "number" && width > 0;
    const hasColor = color != null;
    const hasActiveStyle = style !== undefined && style !== "none";

    // Determine if border is active (any border property suggests a border)
    const hasBorder = hasWidth || hasColor || hasActiveStyle;
    if (!hasBorder) return result;

    let updated = result;

    // Infer bS: "solid" when bW or bC exist but bS doesn't
    if (style === undefined) {
      updated = updated.setIn([layout, BORDER_STYLE], "solid");
    }

    // Infer default bC when border is active but bC missing
    if (!hasColor) {
      updated = updated.setIn([layout, BORDER_COLOR], DEFAULT_BORDER_PROPERTIES.color);
    }

    // Infer default bW when border is active but bW missing
    if (!hasWidth) {
      updated = updated.setIn([layout, BORDER_WIDTH], DEFAULT_BORDER_PROPERTIES.width);
    }

    return updated;
  }, elementData);
};

/**
 * todo: maybe move this elsewhere. Might be more of a multicast thing
 * Pre-inherits and prepares data before mixing.
 * @param {ImmutableMap} elementData
 * @param {ImmutableMap} theme
 * @return {ImmutableMap}
 */
export const prepElementData = (elementData, theme) => {
  const type = elementData.get("type");

  // Normalize legacy Simple border data before processing
  const normalized = type === SIMPLE ? inferBorderPropsForSimple(elementData) : elementData;

  const inheritPropList = INHERIT_PROP_LIST[type];
  if (!inheritPropList) return normalized;

  // applies design settings
  const merged = LAYOUTS.reduce((result, layout) => {
    const designSettingsProps = gatherProps(result, layout, INHERIT_PROP_LIST[type], false, theme);
    return result.set(layout, designSettingsProps.mergeDeep(result.get(layout)));
  }, normalized);

  const inheritedSync = getInheritedSync(normalized, inheritPropList, theme);
  const mergedSync = List(new Set([...merged.get(SYNCED_PROPS, List()), ...inheritedSync]));

  return merged.set(SYNCED_PROPS, mergedSync).set(IS_DEVIATION, isDeviation(normalized));
};

/**
 * Deviates a merged prop, setting the value on desktop and pulling the merged state locally.
 * @param {UpdateBuilder} builder
 * @param {string=} propName - override for propName if different from base.
 */
const deviateMergedProp = (builder, propName) =>
  builder.inheritProp(propName, LAYOUT_DESKTOP).addMergeProp(propName).collectChanges();

/**
 * Deviates a split prop, copying the other layout's data if it is not deviated. Assumes the base deviation update exists.
 * @param {UpdateBuilder} builder
 * @param {ImmutableMap} elementData
  @param {string} propName
  @param {LAYOUTS[number]} layout
 */
const deviateSplitProp = (builder, elementData, propName, layout) => {
  const otherLayout = layout === LAYOUT_DESKTOP ? LAYOUT_MOBILE : LAYOUT_DESKTOP;
  const inheritOtherLayoutProp = getProp(elementData, otherLayout, propName) === undefined;

  // todo: is the reflection actually necessary? My guess is no. Figure this out later.
  // if (!updateOverride) {
  //   builder.redirectBaseUpdate(updateLayout);
  // }

  builder.inheritProp(propName, layout);

  if (inheritOtherLayoutProp) {
    builder.inheritProp(propName, otherLayout);
  }

  return builder;
};

/**
 * todo: could this be smaller?
 * Generates prop deviation updates,
 * @param {ImmutableMap} elementData
 * @param {string} propName
 * @param {LAYOUTS[number]} layout
 * @param {ImmutableMap} theme
 * @returns {UpdateBuilder}
 */
export const deviateProp = (elementData, propName, layout, theme) => {
  // todo: may need to be updated if multiple deviations are found since it assumes the first is the source of truth
  const builder = new UpdateBuilder({ elementData, theme });

  // * parent props. All of this should technically come from theme even though it's pulling local.
  const isPropSub = propIsSub(elementData, propName);
  const isPropParent = propIsParent(elementData, propName);
  const parentPropName = getSubParent(elementData, propName);
  const isParentPropSynced = isSynced(elementData, parentPropName, layout, theme);

  // treat style as synced if it cannot be split
  const isStyleSynced = canStyleSync(elementData)
    ? isSynced(elementData, getTypeStylePropName(elementData), layout)
    : true;
  const isPropSynced = isStyleSynced && isSynced(elementData, propName, layout, theme);

  // only affect other layout if style is synced.
  // checked before isPropSynced, which reports the parent's sync for subs
  if (isStyleSynced && isPropSub && isParentPropSynced) {
    // inherit prop as though it was merged without adding to sync array
    builder.inheritProp(propName, LAYOUT_DESKTOP);
  } else if (isPropSynced) {
    deviateMergedProp(builder, propName);
  } else if (isStyleSynced) {
    deviateSplitProp(builder, elementData, propName, layout);
  }

  // also inherit parent prop value and marge state if base propName is sub.
  if (isStyleSynced && isPropSub) {
    isParentPropSynced
      ? deviateMergedProp(builder, parentPropName)
      : deviateSplitProp(builder, elementData, parentPropName, layout);
  }

  // inherit sub props if propName is parent.
  if (isPropParent) {
    // should never trigger text, but keep in mind if things get weird
    const subProps = gatherSubPropList(propName);
    const layouts = isPropSynced ? [LAYOUT_DESKTOP] : LAYOUTS;

    subProps.forEach((propName) =>
      layouts.forEach((layout) => builder.inheritProp(propName, layout))
    );
  }

  return builder;
};
/**
 * Generates prop deviation updates,
 * @param {ImmutableMap} elementData
 * @param {PropUpdate | Array<PropUpdate>} propInfo
 * @param {ImmutableMap} theme
 * @returns {Array<PropUpdate>}
 */
export const deviatePropFromUpdate = (elementData, propInfo, theme) => {
  const updates = ensureArray(propInfo);
  const [propName, layout] = getPropUpdateDetails(updates[0]);

  return deviateProp(elementData, propName, layout, theme)
    .addBaseUpdate({
      ...updates[0],
    })
    .collectChanges();
};

/** STYLE CHANGE PROCESSES */

/**
 * todo: write tests for this and then update to use UpdateBuilder
 * Syncs style property while preserving + props from both
 * @param {ImmutableMap} elementData
 * @param {LAYOUTS[number]} layout
 * @param {Array<PropUpdate>} propInfo
 * @returns {Array<PropUpdate>}
 */
const syncTextStyle = (elementData, layout, propInfo) => {
  const removeKeys = STYLE_CHANGE_REMOVE_KEYS[TEXT];
  const styleSyncProps = STYLE_SYNC_PROPS[TEXT];

  const updates = [];
  // syncs and preserved + props
  const layoutProps = elementData.get(layout);
  const plusProps = layoutProps.filter(ImmutableUtils.keyIn(styleSyncProps));

  if (layout === LAYOUT_MOBILE) {
    updates.push(...generateRemovePropUpdates(elementData, removeKeys, LAYOUT_DESKTOP));
    // move + props to desktop
    updates.push(
      ...plusProps.reduce((result, value, propName) => {
        result.push({ name: `${LAYOUT_DESKTOP}.${propName}`, value });
        return result;
      }, [])
    );
  }

  updates.push(...generateRemovePropUpdates(elementData, removeKeys, LAYOUT_MOBILE));

  const plusSyncProps = new Set([...plusProps.keys(), TEXT_OVERFLOW, TEXT_STYLE]);

  updates.push(deleteProp(`${LAYOUT_MOBILE}.${TEXT_STYLE}`));

  const syncUpdate = propInfo.find(({ name }) => name === SYNCED_PROPS) ?? {
    name: SYNCED_PROPS,
    value: [],
  };
  syncUpdate.value = Array.from(new Set([...syncUpdate.value, ...Array.from(plusSyncProps)]));
  updates.push(syncUpdate);

  return updates;
};

/**
 * todo: rework this with updateBuilder
 * @param {ImmutableMap} elementData
 * @param {PropUpdate | Array<PropUpdate>} propInfo
 * @param {LAYOUTS[number]} layout
 * @returns {Array<PropUpdate>}
 */
export const applyStyle = (elementData, propInfo, layout) => {
  const updates = ensureArray(propInfo);
  const elementType = elementData.get("type");
  const styleProp = getTypeStylePropName(elementData);

  const isSync = updates.some((update) => update?.name === SYNCED_PROPS);
  const removeKeys = STYLE_CHANGE_REMOVE_KEYS[elementType];

  const baseStyleUpdate = updates.find((singlePropInfo) => {
    const [propName] = getPropUpdateDetails(singlePropInfo);
    return propName.endsWith(styleProp);
  });

  // todo: check if style can sync
  const styleCanSync = canStyleSync(elementData);
  const styleIsSynced = isStyleSynced(elementData);

  const shouldSync =
    (isSync || baseStyleUpdate?.origValue === MIXED) && !styleIsSynced && !styleCanSync;

  const newUpdates = [];

  if (shouldSync) {
    // should only happen for text.
    newUpdates.push(...syncTextStyle(elementData, layout, updates));
  } else {
    // clean up/remove deviated props if set
    if (isDeviation(elementData)) {
      // todo: add canSync here to remove from both layouts. Actually, might just be able to use styleIsSynced
      const removalLayouts = styleIsSynced || !styleCanSync ? LAYOUTS : [layout];

      newUpdates.push(
        ...removalLayouts.flatMap((layout) =>
          generateRemovePropUpdates(elementData, removeKeys, layout)
        )
      );
    }

    const sync = elementData.get(SYNCED_PROPS);

    sync &&
      newUpdates.push({
        name: SYNCED_PROPS,
        value: sync.filter((prop) => !removeKeys.includes(prop)),
        action: UPDATE_ACTION_SET,
      });
  }

  newUpdates.push(
    // redirect update if style is synced or should sync
    styleIsSynced || shouldSync ? redirectUpdate(baseStyleUpdate, LAYOUT_DESKTOP) : baseStyleUpdate
  );

  return newUpdates;
};

// todo: do these belong in multicast utils or some multi element inheritance utils?

export const isValueMixed = (value) =>
  value === MIXED || (typeof value === "string" && value.includes(MIXED));

export const isPropValueMixed = (propName, layout, element) => {
  const propPath = [
    ...(layout ? [layout] : []),
    ...(Array.isArray(propName) ? propName : propName.split(".")),
  ];
  const value = element.getIn(propPath);
  return isValueMixed(value);
};

/**
 *
 * @param {string} propName
 * @param {ImmutableMap} preparedElement
 * @returns {Boolean}
 */
export const isSyncMixed = (propName, preparedElement) =>
  preparedElement.get(MIXED_SYNC, List()).includes(propName);
